import { CheckCircle2, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Plan } from './types'

/**
 * 플랜 비교 표.
 *
 * 기능 목록은 플랜마다 다를 수 있으므로, 모든 플랜에 나타난 기능 이름을
 * 모아 행으로 쓴다. 어떤 플랜에만 있는 기능도 빠지지 않는다.
 */

interface PlanComparisonTableProps {
  plans: Plan[]
}

/** 플랜 순서를 지키면서 기능 이름을 중복 없이 모은다 */
function featureNames(plans: Plan[]): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const plan of plans) {
    for (const feature of plan.features) {
      if (!seen.has(feature.name)) {
        seen.add(feature.name)
        names.push(feature.name)
      }
    }
  }
  return names
}

export default function PlanComparisonTable({
  plans,
}: PlanComparisonTableProps) {
  const { t } = useTranslation()
  const names = featureNames(plans)

  if (plans.length === 0 || names.length === 0) {
    return null
  }

  const columns = `minmax(0,1fr) repeat(${plans.length}, minmax(96px, 110px))`

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900 dark:text-white">
        {t('pricing.compareTitle')}
      </h2>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="min-w-[560px]">
          <div
            className="grid gap-3 border-b border-gray-100 bg-gray-50 px-6 py-3.5 text-[13px] font-bold dark:border-gray-700 dark:bg-gray-900/60"
            style={{ gridTemplateColumns: columns }}
          >
            <span className="font-semibold text-gray-500 dark:text-gray-400">
              {t('pricing.featureColumn')}
            </span>
            {plans.map(plan => (
              <span
                key={plan._id}
                className={`text-center ${
                  plan.isPopular
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {plan.name}
              </span>
            ))}
          </div>

          {names.map(name => (
            <div
              key={name}
              className="grid items-center gap-3 border-b border-gray-100 px-6 py-3 last:border-b-0 dark:border-gray-700"
              style={{ gridTemplateColumns: columns }}
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {name}
              </span>
              {plans.map(plan => {
                const feature = plan.features.find(item => item.name === name)
                return (
                  <div
                    key={plan._id}
                    className="flex items-center justify-center text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-300"
                  >
                    {feature?.included ? (
                      feature.description ? (
                        <span>{feature.description}</span>
                      ) : (
                        <CheckCircle2
                          className="h-5 w-5 text-primary-600 dark:text-primary-400"
                          aria-label={t('common.yes', 'Yes')}
                        />
                      )
                    ) : (
                      <Minus
                        className="h-4 w-4 text-gray-300 dark:text-gray-600"
                        aria-label={t('common.no', 'No')}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
